import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PostScheduleListView, PostScheduleListViewProps } from './PostScheduleList.view';
import { PostScheduleViewProps } from '../PostSchedule.view';
import { PostItemFragment, QueueFilterInput, QueueFilterPostStatus, QueueSortOrder } from '../../../../models/graphql';
import { cloneDeep } from '@apollo/client/utilities';
import { LimitsReachedType } from './PostScheduleList.apollo';
import { compareAsc, parseISO } from 'date-fns';
import { throttle } from 'lodash';

export type PostScheduleListControllerProps = PostScheduleViewProps & {
  pollingTimedOut: boolean;
  fetchMorePostItems: (scrollPosition: 'TOP' | 'BOTTOM') => void;
  fetchSubQueuePosts: (post: PostItemFragment, filters: QueueFilterInput) => void;
  fetchPostsAfterPostMove: (post: PostItemFragment, filters: QueueFilterInput) => void;
  posts: PostItemFragment[];
  limitsReached: LimitsReachedType;
  setLimitsReached: (limits: LimitsReachedType) => void;
  queueFiltersInput: QueueFilterInput;
};

export const PostScheduleListController = (props: PostScheduleListControllerProps): JSX.Element => {
  const leftColRef = useRef<HTMLDivElement>(null);
  const rightColRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const {
    formInstance,
    fetchMorePostItems,
    fetchSubQueuePosts,
    fetchPostsAfterPostMove,
    limitsReached,
    queueFilters,
    posts,
    queueFiltersInput
  } = props;

  const handleFetchMore = (sortOrder: QueueSortOrder, scrollPosition: 'TOP' | 'BOTTOM') => {
    const hasReachedLimit =
      (scrollPosition === 'TOP' && limitsReached.upper) ||
      (scrollPosition === 'BOTTOM' && limitsReached.lower) ||
      (!sortOrder && limitsReached.lower);

    if (!hasReachedLimit) {
      fetchMorePostItems(scrollPosition);
    }
  };

  const throttledFetchMore = throttle(handleFetchMore, 500); // Invoke at most once per 500ms

  const fetchMore = useCallback(
    (...args: Parameters<typeof handleFetchMore>) => {
      throttledFetchMore(...args);
    },
    [throttledFetchMore]
  );

  const getSubQueuePosts = useCallback(
    (post: PostItemFragment) => {
      fetchSubQueuePosts(post, queueFiltersInput);
    },
    [fetchSubQueuePosts, queueFiltersInput]
  );

  const getPostsAfterPostMove = useCallback(
    (post: PostItemFragment) => {
      // setLoadingInitial(true);
      fetchPostsAfterPostMove(post, queueFiltersInput);
    },
    [fetchPostsAfterPostMove, queueFiltersInput]
  );

  // Whenever we change filters, we reset the post checkboxes
  useEffect(() => {
    formInstance.resetField('postItemCheckBox');
  }, [formInstance, props.queueFilters]);

  // Because we can get query for more above or below the current set, we always ensure our ordering is correct
  const sortedPost = useMemo(() => {
    const postItems = cloneDeep(posts);

    return postItems.sort((a, b) => {
      switch (queueFilters.status) {
        case QueueFilterPostStatus.FAILED:
          return compareAsc(parseISO(b.dateFailed), parseISO(a.dateFailed));
        case QueueFilterPostStatus.PAST:
          return compareAsc(parseISO(b.datePosted), parseISO(a.datePosted));
        default:
          return compareAsc(parseISO(a.calculatedLocalDateTime), parseISO(b.calculatedLocalDateTime));
      }
    });
  }, [posts, queueFilters.status]);

  const viewProps: PostScheduleListViewProps = {
    ...props,
    leftColRef,
    rightColRef,
    isDragging,
    setIsDragging,
    categoryFilterId: queueFilters.categoryId || '',
    socialProfileId: queueFilters.socialProfileId || '',
    postTimingFilter: queueFilters.timing,
    fetchMorePostScheduleListItems: fetchMore,
    getSubQueuePosts,
    hasReachedFetchLimit: limitsReached.lower,
    posts: sortedPost,
    getPostsAfterPostMove: getPostsAfterPostMove
  };

  return <PostScheduleListView {...viewProps} />;
};
